// Builds the Shopify Admin style payment summary from the v3 shopify-details order
// (raw Shopify GraphQL shape: money is { shopMoney: { amount } }, connections are { nodes: [] })

const money = set => parseFloat(set?.shopMoney?.amount ?? 0) || 0;
const round = value => Math.round(value * 100) / 100;
const nodes = connection =>
  Array.isArray(connection) ? connection : connection?.nodes || [];

const pluralize = (count, word) => `${count} ${word}${count === 1 ? "" : "s"}`;

const statusLabel = status => {
  if (!status) return null;
  const label = status.toLowerCase().replace(/_/g, " ");
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const longDate = date => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric"
  });
};

// Discount amounts per discount application, for the current (non-refunded) quantities.
// Line item allocations cover the original quantity, so they're scaled down by currentQuantity / quantity.
const buildDiscounts = (order, lineItems, shippingLines) => {
  const names = {};
  nodes(order.discountApplications).forEach(application => {
    names[application.index] =
      application.code || application.title || "Discount";
  });

  const amounts = {};
  const addAllocation = (allocation, ratio) => {
    const index = allocation.discountApplication?.index;
    amounts[index] =
      (amounts[index] || 0) + money(allocation.allocatedAmountSet) * ratio;
  };

  lineItems.forEach(lineItem => {
    const ratio = lineItem.quantity
      ? lineItem.currentQuantity / lineItem.quantity
      : 0;
    (lineItem.discountAllocations || []).forEach(allocation =>
      addAllocation(allocation, ratio)
    );
  });
  shippingLines.forEach(shippingLine => {
    (shippingLine.discountAllocations || []).forEach(allocation =>
      addAllocation(allocation, 1)
    );
  });

  const discounts = Object.keys(amounts)
    .map(index => ({
      title: names[index] || "Discount",
      amount: round(amounts[index])
    }))
    .filter(discount => discount.amount > 0);

  // Scaling refunded quantities can leave rounding cents; true up the largest row to Shopify's total
  const shopifyTotal = money(order.currentTotalDiscountsSet);
  const difference = round(
    shopifyTotal - discounts.reduce((sum, d) => sum + d.amount, 0)
  );
  if (
    discounts.length &&
    difference !== 0 &&
    Math.abs(difference) <= 0.01 * lineItems.length
  ) {
    const largest = discounts.reduce((a, b) => (b.amount > a.amount ? b : a));
    largest.amount = round(largest.amount + difference);
  }

  return discounts;
};

const buildRefunds = order =>
  (order.refunds || [])
    .map(refund => {
      const itemCount = nodes(refund.refundLineItems).reduce(
        (sum, refundLineItem) => sum + (refundLineItem.quantity || 0),
        0
      );
      const reason = refund.note || "no reason provided";
      return {
        description: itemCount
          ? `${pluralize(itemCount, "item")}, ${reason}`
          : reason.charAt(0).toUpperCase() + reason.slice(1),
        amount: money(refund.totalRefundedSet)
      };
    })
    .filter(refund => refund.amount > 0);

export default {
  build(order) {
    if (!order) return null;

    const lineItems = nodes(order.lineItems);
    const shippingLines = nodes(order.shippingLines);
    const taxLines = order.taxLines || [];

    const itemCount = lineItems.reduce(
      (sum, lineItem) => sum + (lineItem.currentQuantity || 0),
      0
    );
    // Subtotal and shipping are Shopify's own amounts after discounts, so the discount
    // rows are already included in them and are shown for reference, not subtracted
    const discounts = buildDiscounts(order, lineItems, shippingLines);

    const refunds = buildRefunds(order);
    const hasRefunds = (order.refunds || []).length > 0;

    return {
      status: statusLabel(order.displayFinancialStatus),
      original: hasRefunds
        ? {
            date: longDate(order.createdAt),
            amount: money(order.totalPriceSet)
          }
        : null,
      subtotal: {
        description: pluralize(itemCount, "item"),
        amount: money(order.currentSubtotalPriceSet)
      },
      discounts,
      shipping: shippingLines.length
        ? {
            description: shippingLines
              .map(shippingLine => shippingLine.title)
              .join(", "),
            amount: money(order.currentShippingPriceSet)
          }
        : null,
      taxes: {
        amount: money(order.currentTotalTaxSet),
        lines: taxLines.map(taxLine => ({
          description: `${taxLine.title} ${taxLine.ratePercentage}%`,
          amount: money(taxLine.priceSet)
        }))
      },
      total: money(order.currentTotalPriceSet),
      paid: money(order.totalReceivedSet),
      refunds,
      netPayment: hasRefunds ? money(order.netPaymentSet) : null
    };
  }
};
